class GeolocationPopup extends HTMLElement {
  initializeElements() {
    if (this.elementsInitialized) {
      return;
    }

    this.elementsInitialized = true;
    this.cookieName = 'geo-stay';
    this.config = null;
    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.suggestionView = this.querySelector('[data-view="suggestion"]');
    this.listView = this.querySelector('[data-view="list"]');
    this.listEl = this.querySelector('[data-geo-list]');
    this.titleEl = this.querySelector(
      '[data-view="suggestion"] .geolocation-popup__title',
    );
    this.bodyEl = this.querySelector('.geolocation-popup__body');
    this.goButton = this.querySelector('.js-geo-go');
    this.stayButton = this.querySelector('.js-geo-stay');
    this.chooseButton = this.querySelector('.js-geo-choose');
    this.backButton = this.querySelector('.js-geo-back');
    this.closeButton =
      this.querySelector('.geolocation-popup__close') ||
      this.querySelector('[id^="ModalClose-"]');
    this.suggestedStore = null;
    this.detectedCode = null;
    this.openedFromSuggestion = false;

    if (this.interactionsBound) {
      return;
    }

    this.interactionsBound = true;

    this.addEventListener('click', (event) => {
      if (
        event.target.closest('.js-geo-stay') ||
        event.target.closest('.geolocation-popup__close') ||
        event.target.closest('[id^="ModalClose-"]')
      ) {
        event.preventDefault();
        this.onStay(event);
        return;
      }

      if (event.target.closest('.js-geo-choose')) {
        event.preventDefault();
        this.showListView();
        return;
      }

      if (event.target.closest('.js-geo-back')) {
        event.preventDefault();
        this.showSuggestionView();
        return;
      }

      if (event.target.closest('.js-geo-go')) {
        this.onGo(event);
        return;
      }

      if (event.target === this) {
        this.onStay(event);
      }
    });

    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') {
        this.onStay();
      }
    });
  }

  moveToBody() {
    if (this.moved) {
      return;
    }

    this.moved = true;
    document.body.appendChild(this);
  }

  ensureElements() {
    if (!this.elementsInitialized) {
      this.initializeElements();
      return;
    }

    if (!this.dialogHolder) {
      this.dialogHolder = this.querySelector('[role="dialog"]');
    }

    if (!this.suggestionView) {
      this.suggestionView = this.querySelector('[data-view="suggestion"]');
    }

    if (!this.listView) {
      this.listView = this.querySelector('[data-view="list"]');
    }

    if (!this.listEl) {
      this.listEl = this.querySelector('[data-geo-list]');
    }

    if (!this.titleEl) {
      this.titleEl = this.querySelector(
        '[data-view="suggestion"] .geolocation-popup__title',
      );
    }

    if (!this.bodyEl) {
      this.bodyEl = this.querySelector('.geolocation-popup__body');
    }

    if (!this.goButton) {
      this.goButton = this.querySelector('.js-geo-go');
    }

    if (!this.stayButton) {
      this.stayButton = this.querySelector('.js-geo-stay');
    }

    if (!this.chooseButton) {
      this.chooseButton = this.querySelector('.js-geo-choose');
    }

    if (!this.backButton) {
      this.backButton = this.querySelector('.js-geo-back');
    }

    if (!this.closeButton) {
      this.closeButton =
        this.querySelector('.geolocation-popup__close') ||
        this.querySelector('[id^="ModalClose-"]');
    }
  }

  show() {
    this.ensureElements();

    if (this.suggestionView?.hidden && this.listView?.hidden) {
      this.showSuggestionView();
    }

    bodyScroll.lock(this.dialogHolder);
    this.setAttribute('open', '');
    document.documentElement.classList.add('geolocation-popup-open');
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();
  }

  hide() {
    bodyScroll.unlock(this.dialogHolder);
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    document.documentElement.classList.remove('geolocation-popup-open');
    removeTrapFocus(this.dialogHolder);
    window.pauseAllMedia();
  }

  connectedCallback() {
    if (this.dataset.geoBooted === 'true') {
      return;
    }

    this.dataset.geoBooted = 'true';
    this.moveToBody();
    this.initializeElements();

    const start = () => {
      this.boot();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  async boot() {
    this.config = await this.waitForConfig();

    if (this.dataset.openInDesignMode === 'true') {
      this.setupDesignMode();
      return;
    }

    await this.init();
  }

  loadConfig() {
    const configEl =
      this.querySelector('[data-geolocation-config]') ||
      document.getElementById('CountryLinksConfig') ||
      document.querySelector('[data-geolocation-config]');

    if (!configEl) {
      return null;
    }

    try {
      return JSON.parse(configEl.textContent.trim());
    } catch (error) {
      console.warn('Geolocation popup: invalid config', error);
      return null;
    }
  }

  waitForConfig() {
    return new Promise((resolve) => {
      const readConfig = () => {
        const config = this.loadConfig();

        if (config?.stores?.length > 1) {
          return config;
        }

        return null;
      };

      const existing = readConfig();

      if (existing) {
        resolve(existing);
        return;
      }

      let attempts = 0;
      const maxAttempts = 40;
      const observer = new MutationObserver(() => {
        const config = readConfig();

        if (config) {
          observer.disconnect();
          resolve(config);
        }
      });

      observer.observe(this, {
        childList: true,
        subtree: true,
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });

      const interval = window.setInterval(() => {
        attempts += 1;
        const config = readConfig();

        if (config || attempts >= maxAttempts) {
          window.clearInterval(interval);
          observer.disconnect();
          resolve(config);
        }
      }, 50);
    });
  }

  setupDesignMode() {
    if (!this.config) {
      return;
    }

    this.suggestedStore =
      this.config.stores.find(
        (store) => store.link !== this.config.currentStore.link,
      ) || this.config.stores[0];

    this.detectedCode = this.suggestedStore.countryCodes[0] || 'FR';
    this.renderCountryList();
    this.renderSuggestion();
    this.showSuggestionView();
    this.show();

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this)) {
        this.renderSuggestion();
        this.showSuggestionView();
        this.show();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) {
        this.hide();
      }
    });
  }

  async init() {
    if (!this.config || this.config.stores.length < 2) {
      this.notifyReady(false);
      return;
    }

    if (getCookie(this.cookieName) !== null) {
      this.notifyReady(false);
      return;
    }

    this.ensureElements();
    this.renderCountryList();

    try {
      this.detectedCode = await this.fetchCountryCode();
      this.suggestedStore = this.findStoreForCountry(this.detectedCode);

      if (
        !this.suggestedStore ||
        this.normalizeUrl(this.suggestedStore.link) ===
          this.normalizeUrl(this.config.currentStore.link)
      ) {
        this.notifyReady(false);
        return;
      }

      this.renderSuggestion();
      this.showSuggestionView();
      this.show();
      this.notifyReady(true);
    } catch (error) {
      console.warn('Geolocation popup:', error);
      this.notifyReady(false);
    }
  }

  normalizeUrl(url) {
    return String(url || '')
      .trim()
      .replace(/\/+$/, '')
      .toLowerCase();
  }

  renderCountryList() {
    this.ensureElements();

    const listEl = this.querySelector('[data-geo-list]');

    if (!listEl || !this.config) {
      return;
    }

    this.listEl = listEl;

    const alternateStores = this.config.stores.filter(
      (store) =>
        this.normalizeUrl(store.link) !==
        this.normalizeUrl(this.config.currentStore.link),
    );

    listEl.innerHTML = alternateStores
      .map(
        (store) => `
          <li class="geolocation-popup__list-item">
            <a href="${store.link}" class="geolocation-popup__list-link">
              ${
                store.icon
                  ? `<span class="geolocation-popup__list-flag"><img src="${store.icon}" alt="" width="24" height="24" loading="lazy"></span>`
                  : ''
              }
              <span class="geolocation-popup__list-name">${store.name}</span>
              <span class="geolocation-popup__list-chevron" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="23.426" height="23.666" viewBox="0 0 23.426 23.666">
                  <g transform="translate(0 0.354)">
                    <path d="M0,0H22.1" transform="translate(0 11.479)" fill="none" stroke="currentColor" stroke-width="1"/>
                    <path d="M0,22.959,11.479,11.479,0,0" transform="translate(11.239)" fill="none" stroke="currentColor" stroke-width="1"/>
                  </g>
                </svg>
              </span>
            </a>
          </li>
        `,
      )
      .join('');
  }

  async fetchCountryCode() {
    const response = await fetch('https://get.geojs.io/v1/ip/country.json');

    if (!response.ok) {
      throw new Error('Geolocation request failed');
    }

    const data = await response.json();

    if (!data.country) {
      throw new Error('Geolocation country missing');
    }

    return data.country.toUpperCase();
  }

  findStoreForCountry(code) {
    const directMatch = this.config.stores.find((store) =>
      store.countryCodes.includes(code),
    );

    if (directMatch) {
      return directMatch;
    }

    return (
      this.config.stores.find((store) => store.countryCodes.length === 0) ||
      null
    );
  }

  getPopupLocale(code = this.detectedCode) {
    const localeMap = this.config?.localeByCountry || {};
    const byCountry = code ? localeMap[String(code).toUpperCase()] : null;

    if (byCountry) {
      return byCountry;
    }

    const defaultLocale = String(this.config?.defaultLocale || 'en')
      .toLowerCase()
      .split(/[-_]/)[0];

    return defaultLocale || 'en';
  }

  getStrings(code = this.detectedCode) {
    const locale = this.getPopupLocale(code);
    const byLocale = this.config?.stringsByLocale || {};

    return byLocale[locale] || byLocale.en || this.config?.strings || {};
  }

  getCountryName(code, locale = this.getPopupLocale(code)) {
    try {
      const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
      return displayNames.of(code);
    } catch (error) {
      return code;
    }
  }

  replacePlaceholders(template, replacements) {
    return Object.entries(replacements).reduce((result, [key, value]) => {
      const safeValue = value ?? '';

      return result
        .replaceAll(`{${key}}`, safeValue)
        .replaceAll(`{{ ${key} }}`, safeValue)
        .replaceAll(`{{${key}}}`, safeValue);
    }, String(template || ''));
  }

  applyStaticLabels(strings = this.getStrings()) {
    this.ensureElements();

    const choosePrefix = this.querySelector(
      '[data-view="suggestion"] .geolocation-popup__footer',
    );
    const chooseLink = this.querySelector(
      '[data-view="suggestion"] .js-geo-choose',
    );
    const listTitle = this.querySelector(
      '[data-view="list"] .geolocation-popup__title',
    );

    if (choosePrefix && chooseLink) {
      const prefixTextNode = Array.from(choosePrefix.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE,
      );

      if (prefixTextNode) {
        prefixTextNode.textContent = `${strings.chooseCountryPrefix || ''} `;
      }

      chooseLink.textContent = strings.chooseCountryLink || '';
    }

    if (listTitle) {
      listTitle.textContent = strings.listTitle || '';
    }

    if (this.backButton) {
      this.backButton.textContent = strings.back || '';
    }
  }

  renderSuggestion() {
    if (!this.suggestedStore || !this.detectedCode || !this.config) {
      return;
    }

    this.ensureElements();

    const strings = this.getStrings();
    const countryName = this.getCountryName(this.detectedCode);
    const titleEl = this.querySelector(
      '[data-view="suggestion"] .geolocation-popup__title',
    );
    const bodyEl = this.querySelector('[data-view="suggestion"] .geolocation-popup__body');
    const goButton = this.querySelector('[data-view="suggestion"] .js-geo-go');
    const stayButton = this.querySelector('[data-view="suggestion"] .js-geo-stay');

    this.applyStaticLabels(strings);

    if (titleEl) {
      titleEl.textContent = this.replacePlaceholders(strings.suggestionTitle, {
        country: countryName,
      });
    }

    if (bodyEl) {
      bodyEl.textContent = this.replacePlaceholders(strings.suggestionBody, {
        store_name: this.suggestedStore.name,
      });
    }

    if (goButton) {
      goButton.href = this.suggestedStore.link;

      const goLabel = goButton.querySelector('span');

      if (goLabel) {
        goLabel.textContent = this.replacePlaceholders(strings.goToStore, {
          store_name: this.suggestedStore.name,
        });
      }
    }

    if (stayButton) {
      stayButton.textContent = this.replacePlaceholders(strings.stayOnStore, {
        store_name: this.config.currentStore.name,
      });
    }
  }

  showSuggestionView() {
    this.ensureElements();
    this.openedFromSuggestion = false;

    if (this.suggestionView) {
      this.suggestionView.hidden = false;
    }

    if (this.listView) {
      this.listView.hidden = true;
    }

    if (this.backButton) {
      this.backButton.hidden = true;
    }
  }

  showListView() {
    this.ensureElements();
    this.renderCountryList();
    this.applyStaticLabels();
    this.openedFromSuggestion = !this.suggestionView?.hidden;

    if (this.suggestionView) {
      this.suggestionView.hidden = true;
    }

    if (this.listView) {
      this.listView.hidden = false;
    }

    if (this.backButton) {
      this.backButton.hidden = !this.openedFromSuggestion;
    }
  }

  onGo(event) {
    event.preventDefault();

    if (this.suggestedStore?.link) {
      window.location.href = this.suggestedStore.link;
    }
  }

  onStay() {
    if (this.config) {
      setCookie(this.cookieName, 'true', this.config.cookieDays);
    }

    this.hide();
  }

  async openCountryList() {
    if (!this.config) {
      this.config = await this.waitForConfig();
    }

    if (!this.config || this.config.stores.length < 2) {
      return;
    }

    this.ensureElements();
    this.renderCountryList();
    this.applyStaticLabels();
    this.openedFromSuggestion = false;

    if (this.suggestionView) {
      this.suggestionView.hidden = true;
    }

    if (this.listView) {
      this.listView.hidden = false;
    }

    if (this.backButton) {
      this.backButton.hidden = true;
    }

    this.show();
  }

  notifyReady(displayed) {
    document.dispatchEvent(
      new CustomEvent('geolocationPopupReady', {
        detail: { displayed },
      }),
    );
  }
}

customElements.define('geolocation-popup', GeolocationPopup);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-country-popup]');

  if (!trigger) {
    return;
  }

  event.preventDefault();

  const popup = document.querySelector('geolocation-popup');

  if (popup?.openCountryList) {
    popup.openCountryList();
  }
});
